import React, { Component } from 'react';
import { Button, Form, Badge, Card } from 'react-bootstrap';
import Select from 'components/controls/select';
import { GoPlus, GoTrash } from 'react-icons/go';
import { WORKSPACE_TYPES, PACK_SOURCES } from '../../domain/schema';

/**
 * WorkspaceForm — structured editor for a single workspace template.
 *
 * Sections:
 *   - Type (currently only "local_folder")
 *   - Repos: list of { url, branch } with add/remove
 *   - Skill packs: list of { source, name } with add/remove
 *
 * All changes call onChange(updatedWorkspace) immediately; the parent
 * WorkspacesSurface owns the save lifecycle.
 *
 * Props:
 *   name        — the template key string
 *   workspace   — the workspace template object { type, repos, packs }
 *   onChange    — (updatedWorkspace) => void
 */

const wsTypeOptions = WORKSPACE_TYPES.map(t => ({ value: t, label: t }));
const packSourceOptions = PACK_SOURCES.map(s => ({ value: s, label: s }));

class WorkspaceForm extends Component {
  _setField(key, val) {
    const { workspace, onChange } = this.props;
    if (onChange) onChange({ ...workspace, [key]: val });
  }

  _updateRepo(i, field, val) {
    const { workspace, onChange } = this.props;
    const repos = [...(workspace.repos || [])];
    repos[i] = { ...repos[i], [field]: val };
    if (onChange) onChange({ ...workspace, repos });
  }

  _removeRepo(i) {
    const { workspace, onChange } = this.props;
    const repos = (workspace.repos || []).filter((_, idx) => idx !== i);
    if (onChange) onChange({ ...workspace, repos });
  }

  _addRepo() {
    const { workspace, onChange } = this.props;
    const repos = [...(workspace.repos || []), { url: '', branch: 'main' }];
    if (onChange) onChange({ ...workspace, repos });
  }

  _updatePack(i, field, val) {
    const { workspace, onChange } = this.props;
    const packs = [...(workspace.packs || [])];
    packs[i] = { ...packs[i], [field]: val };
    if (onChange) onChange({ ...workspace, packs });
  }

  _removePack(i) {
    const { workspace, onChange } = this.props;
    const packs = (workspace.packs || []).filter((_, idx) => idx !== i);
    if (onChange) onChange({ ...workspace, packs });
  }

  _addPack() {
    const { workspace, onChange } = this.props;
    const packs = [...(workspace.packs || []), { source: 'plugin', name: '' }];
    if (onChange) onChange({ ...workspace, packs });
  }

  render() {
    const { name, workspace } = this.props;
    if (!workspace) return null;

    const repos = workspace.repos || [];
    const packs = workspace.packs || [];
    const currentType = workspace.type || 'local_folder';

    return (
      <div className="mc-ws-form">
        {/* Header row: name + type badge */}
        <div className="mc-ws-form-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <code style={{ fontSize: '0.93rem', fontWeight: 700 }}>{name}</code>
            <Badge variant="secondary" className="title-tag-badge">{currentType}</Badge>
          </div>
        </div>

        {/* Type */}
        <Form.Group className="mc-ws-field-group">
          <Form.Label><strong>Type</strong></Form.Label>
          <Select
            options={wsTypeOptions}
            value={wsTypeOptions.find(o => o.value === currentType) || wsTypeOptions[0]}
            onChange={opt => this._setField('type', opt ? opt.value : 'local_folder')}          />
        </Form.Group>

        {/* Repos */}
        <Card className="mb-3">
          <Card.Body>
            <Card.Text as="h6">Git repos / branches</Card.Text>
            <hr />
            {repos.length === 0 && (
              <p className="text-muted" style={{ fontSize: '0.8rem', fontStyle: 'italic', marginBottom: 8 }}>
                No repos configured.
              </p>
            )}
            {repos.map((r, i) => (
              <div key={i} className="mc-ws-repo-row">
                <Form.Control
                  size="sm"
                  type="text"
                  style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}
                  value={r.url || ''}
                  placeholder="git@github.com:org/repo.git"
                  onChange={e => this._updateRepo(i, 'url', e.target.value)}
                />
                <Form.Control
                  size="sm"
                  type="text"
                  style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}
                  value={r.branch || ''}
                  placeholder="main"
                  onChange={e => this._updateRepo(i, 'branch', e.target.value)}
                />
                <Button
                  variant="danger"
                  size="sm"
                  className="btn-icon"
                  title="Remove"
                  onClick={() => this._removeRepo(i)}
                >
                  <GoTrash />
                </Button>
              </div>
            ))}
            <Button variant="light" size="sm" style={{ marginTop: 4 }} onClick={() => this._addRepo()}>
              <GoPlus /> Add repo
            </Button>
          </Card.Body>
        </Card>

        {/* Skill packs */}
        <Card className="mb-3">
          <Card.Body>
            <Card.Text as="h6">Skill packs</Card.Text>
            <hr />
            {packs.length === 0 && (
              <p className="text-muted" style={{ fontSize: '0.8rem', fontStyle: 'italic', marginBottom: 8 }}>
                No skill packs configured.
              </p>
            )}
            {packs.map((p, i) => (
              <div key={i} className="mc-ws-pack-row">
                <Select
                  options={packSourceOptions}
                  value={packSourceOptions.find(o => o.value === (p.source || 'plugin')) || packSourceOptions[0]}
                  onChange={opt => this._updatePack(i, 'source', opt ? opt.value : 'plugin')}                />
                <Form.Control
                  size="sm"
                  type="text"
                  style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}
                  value={p.name || ''}
                  placeholder="skill name or path"
                  onChange={e => this._updatePack(i, 'name', e.target.value)}
                />
                <Button
                  variant="danger"
                  size="sm"
                  className="btn-icon"
                  title="Remove"
                  onClick={() => this._removePack(i)}
                >
                  <GoTrash />
                </Button>
              </div>
            ))}
            <Button variant="light" size="sm" style={{ marginTop: 4 }} onClick={() => this._addPack()}>
              <GoPlus /> Add skill pack
            </Button>
          </Card.Body>
        </Card>

        <Form.Text muted>
          <code>local_folder</code> is the first workspace type;
          set per mission/cell/step and inherited by branched cells.
        </Form.Text>
      </div>
    );
  }
}

export default WorkspaceForm;
